<template>
  <Modal @close="$emit('close')">
    <template #icon>
      <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-5 h-5 text-slate-400 shrink-0">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
    />
</svg>
    </template>

    <template #header>
      {{ $t('SelfPerform.PlantDashboard.RecordCommentModal.title') }}
    </template>

    <template #body>
      <div class="flex flex-col h-full min-h-0">
        <form
          class="px-1"
          @submit.prevent="saveComment"
        >
          <label class="sr-only">
            {{ $t('SelfPerform.PlantDashboard.RecordCommentModal.label.comment') }}
          </label>

          <AppTextArea
            v-model="recordForm.form.comment"
            name="comment"
            rows="3"
            data-test="comment"
            class="w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            :placeholder="$t('SelfPerform.PlantDashboard.RecordCommentModal.placeholder.comment')"
            :error="recordForm.getErrors('comment')"
          />

          <div class="mt-2 flex">
            <button
              type="submit"
              class="btn btn--primary-blue"
              data-test="update-record"
              :disabled="commentRecordAction.loading.value || !recordForm.form.comment?.trim()"
            >
              {{ $t('SelfPerform.PlantDashboard.RecordCommentModal.button.saveComment') }}
            </button>
          </div>
        </form>

        <div
          v-if="record.comments.length"
          class="mt-4 overflow-y-auto w-full max-h-80 space-y-5"
        >
          <article
            v-for="comment in record.comments"
            :key="comment.id"
            class="flex gap-3"
          >
            <div
              class="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white bg-blue-800"
              aria-hidden="true"
            >
              {{ initials(comment.creator?.name) }}
            </div>

            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span class="text-sm font-semibold text-slate-800">
                  {{ comment.creator?.name }}
                </span>

                <time class="text-xs text-blue-600">
                  {{ formatCommentDate(comment.createdAt) }}
                </time>
              </div>

              <div class="mt-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 whitespace-pre-wrap break-words">
                {{ comment.body }}
              </div>
            </div>
          </article>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-end">
        <button
          type="button"
          class="btn btn--secondary-blue"
          @click.prevent="$emit('close')"
        >
          {{ $t('SelfPerform.PlantDashboard.RecordCommentModal.button.close') }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import { useFormData } from '@/composables/useFormData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { usePlantDailyRecordsStore } from '@/store/plantDailyRecord';
import Icon from '@/svgs/Icon.vue';
import { computed } from 'vue';

const props = defineProps<{
  projectId: Id;
  recordId: Id;
}>();

const emits = defineEmits<{
  (e: 'close');
}>();

const recordForm = useFormData({
  comment: '',
});

const plantDailyRecordsStore = usePlantDailyRecordsStore();

const record = computed(() => {
  return plantDailyRecordsStore.models.with('comments', (query) => {
    query.with('creator');
  }).with('plant').find(props.recordId);
});

plantDailyRecordsStore.fetchRecord(props.recordId).then(() => {
  plantDailyRecordsStore.models.with('comments', (query) => {
    query.with('creator');
  }).load([record.value]);
});

const commentRecordAction = useStoreApiAction(plantDailyRecordsStore.comment);

const initials = (name?: string) => {
  if(!name) {
    return '?';
  }

  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
};

const formatCommentDate = (date: Date) => {
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const saveComment = () => {
  recordForm.resetErrors();

  commentRecordAction.request(record.value.id, recordForm.form).then(() => {
    recordForm.resetData();
  }).catch((error) => {
    recordForm.setErrors(error.data);
  });
};
</script>

<style scoped></style>
